import { useState } from "react";
import { Button, useNotify } from "react-admin";
import { Grid, MenuItem, TextField, Tooltip, Typography, Chip } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockIcon from "@mui/icons-material/Lock";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import dataProvider from "server/dataProvider";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { studentRequests } from "api/studentApi";
import { CurriculumDisciplineTypeLabels, IndividualCurriculumStatusChoices } from "utils/choices";
import { CurriculumDisicplineTypes, IndividualCurriculumStatus } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";
import GroupPickerDialog from "./GroupPickerDialog";
import { isCourseScheduled } from "./utils";

const toDraft = row => ({
  status: row.status || "",
  credits: row.credits ?? "",
  lecturesHours: row.lecturesHours ?? "",
  exercisesHours: row.exercisesHours ?? "",
  independentWorkHours: row.independentWorkHours ?? "",
  grade: row.grade ?? "",
  note: row.note ?? "",
});

const toNullableNumber = value => (value === "" || value == null ? null : Number(value));

const allowsGrade = status => Number(status) === IndividualCurriculumStatus.SignedOffWithExam;

const numericFields = ["status", "credits", "lecturesHours", "exercisesHours", "independentWorkHours", "grade"];

const normalize = draft => ({
  ...draft,
  ...Object.fromEntries(
    numericFields.map(field => [field, draft[field] === "" ? "" : Number(draft[field])]),
  ),
  note: draft.note ?? "",
});

const DisciplineRow = ({ educationId, row, onSaved }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const [draft, setDraft] = useState(() => toDraft(row));
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(normalize(draft)) !== JSON.stringify(normalize(toDraft(row)));
  const gradeEnabled = allowsGrade(draft.status);
  const byCourse = isCourseScheduled(row);
  const periodLabel = byCourse ? `курс ${row.course}` : `семестър ${row.semester}`;
  const typeName =
    row.type === CurriculumDisicplineTypes.Regular
      ? null
      : CurriculumDisciplineTypeLabels.find(choice => choice.id === row.type)?.name;

  const set = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  const handleStatusChange = value => {
    setDraft(prev => ({ ...prev, status: value, grade: allowsGrade(value) ? prev.grade : "" }));
  };

  const handleCancel = () => setDraft(toDraft(row));

  const handleClear = async () => {
    await dataProvider.customRequest(
      byCourse
        ? studentRequests.clearIndividualCurriculumDisciplineByCourse(educationId, row.disciplineId, row.course)
        : studentRequests.clearIndividualCurriculumDiscipline(educationId, row.disciplineId, row.semester),
      null,
      "DELETE",
    );
    setDraft(toDraft({}));
    notify("Успешно изчистен ред от индивидуалния учебен план", { type: "success" });
    onSaved();
  };

  const handleSave = async () => {
    if (!draft.status) {
      notify("Моля изберете статус!", { type: "error" });
      return;
    }
    if (gradeEnabled && (draft.grade === "" || Number(draft.grade) < 3 || Number(draft.grade) > 6)) {
      notify("Оценката е задължителна и трябва да е между 3 и 6.", { type: "error" });
      return;
    }
    setSaving(true);
    try {
      await dataProvider.customRequest(
        byCourse
          ? studentRequests.saveIndividualCurriculumDisciplineByCourse(educationId, row.disciplineId, row.course)
          : studentRequests.saveIndividualCurriculumDiscipline(educationId, row.disciplineId, row.semester),
        {
          row: {
            status: Number(draft.status),
            credits: toNullableNumber(draft.credits),
            lecturesHours: toNullableNumber(draft.lecturesHours),
            exercisesHours: toNullableNumber(draft.exercisesHours),
            independentWorkHours: toNullableNumber(draft.independentWorkHours),
            grade: gradeEnabled ? Number(draft.grade) : null,
            note: draft.note?.trim() || null,
          },
        },
        "PUT",
      );
      notify("Успешно записан ред от индивидуалния учебен план", { type: "success" });
      onSaved();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid
      container
      spacing={1}
      alignItems="center"
      sx={{ mb: 1, opacity: row.isEditable ? 1 : 0.6 }}
    >
      <Grid
        item
        xs={12}
        md={2}
      >
        <Typography sx={{ wordBreak: "break-word" }}>{row.disciplineName}</Typography>
        {typeName && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
          >
            {typeName}
          </Typography>
        )}
      </Grid>
      <Grid
        item
        xs={4}
        md={0.75}
      >
        <Typography align="center">{row.curriculumCredits ?? "-"}</Typography>
      </Grid>
      <Grid
        item
        xs={4}
        md={0.75}
      >
        <Typography align="center">{row.curriculumLecturesHours}</Typography>
      </Grid>
      <Grid
        item
        xs={4}
        md={0.75}
      >
        <Typography align="center">{row.curriculumExercisesHours}</Typography>
      </Grid>
      <Grid
        item
        xs={4}
        md={0.75}
      >
        <TextField
          value={draft.credits}
          onChange={e => set("credits", e.target.value)}
          label="Кредити пред. у-т"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 0 } }}
          disabled={!row.isEditable}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={4}
        md={0.75}
      >
        <TextField
          value={draft.lecturesHours}
          onChange={e => set("lecturesHours", e.target.value)}
          label="Лекции пред. у-т"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 0 } }}
          disabled={!row.isEditable}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={4}
        md={0.75}
      >
        <TextField
          value={draft.exercisesHours}
          onChange={e => set("exercisesHours", e.target.value)}
          label="Упражнения пред. у-т"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 0 } }}
          disabled={!row.isEditable}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={4}
        md={0.75}
      >
        <TextField
          value={draft.independentWorkHours}
          onChange={e => set("independentWorkHours", e.target.value)}
          label="Извънауд. заетост"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 0 } }}
          disabled={!row.isEditable}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={1.25}
      >
        <TextField
          value={draft.status}
          onChange={e => handleStatusChange(e.target.value)}
          select
          label="Статус"
          size="small"
          disabled={!row.isEditable}
          fullWidth
        >
          {IndividualCurriculumStatusChoices.map(choice => (
            <MenuItem
              key={choice.id}
              value={choice.id}
            >
              {choice.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid
        item
        xs={3}
        md={0.75}
      >
        <TextField
          value={draft.grade}
          onChange={e => set("grade", e.target.value)}
          label="Оценка"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 3, max: 6, step: 0.01 } }}
          disabled={!row.isEditable || !gradeEnabled}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={9}
        md={1.25}
      >
        <TextField
          value={draft.note}
          onChange={e => set("note", e.target.value)}
          label="Коментар"
          size="small"
          inputProps={{ maxLength: 100 }}
          disabled={!row.isEditable}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={1.5}
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}
      >
        {row.isEditable && (
          <>
            <Button
              label="Запази"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!dirty || saving}
            />
            <Button
              label="Откажи"
              startIcon={<CloseIcon />}
              onClick={handleCancel}
              disabled={!dirty || saving}
            />
            {row.status > IndividualCurriculumStatus.None && (
              <ConfirmModal
                onConfirm={handleClear}
                titleContent="Изчистване на ред"
                message={`Сигурни ли сте, че искате да изчистите въведените данни за "${row.disciplineName}" за ${periodLabel}?`}
                triggerButton={
                  <Button
                    label="Изчисти"
                    startIcon={<DeleteOutlineIcon />}
                    disabled={saving}
                  />
                }
              />
            )}
          </>
        )}
        {!row.isEditable && (
          <Tooltip title="Заключено — студентът вече има заверка или оценка от изпит по тази дисциплина">
            <Chip
              icon={<LockIcon />}
              label="Заключено"
              size="small"
              variant="outlined"
            />
          </Tooltip>
        )}
        {row.canPickGroup && !dirty && (
          <GroupPickerDialog
            educationId={educationId}
            row={row}
            onSaved={onSaved}
            triggerButton={
              <Button
                label={row.assignedStudyGroup ? `Група ${row.assignedStudyGroup}` : "Избери група"}
                startIcon={<EditCalendarIcon />}
              />
            }
          />
        )}
        {row.assignedStudyGroup && !row.canPickGroup && (
          <Chip
            label={`Група ${row.assignedStudyGroup}`}
            size="small"
          />
        )}
      </Grid>
    </Grid>
  );
};

export default DisciplineRow;
